import React, { useState, useEffect, useCallback } from "react";
import {
  getStories,
  getStoryById,
  createStory,
  updateStory,
  deleteStory
} from "../api/storiesAPI";
import Input from "./Input";
import Select from "./Select";

const StoriesManagement = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    is_active: undefined
  });

  // Fetch stories
  const fetchStories = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: pagination.limit,
        ...(filters.is_active !== undefined && { is_active: filters.is_active })
      };
      const data = await getStories(params);
      setStories(data.items || []);
      setPagination(prev => ({
        page: data.page || page,
        limit: data.size || prev.limit,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      console.error("Error fetching stories:", error);
      setError(`Ошибка загрузки сториз: ${error.message}`);
      setStories([]);
    }
    setLoading(false);
  }, [pagination.limit, filters.is_active]);

  useEffect(() => {
    fetchStories(1);
  }, [fetchStories]);

  // Form handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      title: "",
      kz_title: "",
      eng_title: "",
      ru_title: "",
      ch_title: "",
      image_url: "",
      priority: 0,
      is_active: true
    });
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEdit = async (item) => {
    try {
      setLoading(true);
      const storyData = await getStoryById(item.id);
      setEditingItem(storyData);
      setFormData({
        title: storyData.title || "",
        kz_title: storyData.kz_title || "",
        eng_title: storyData.eng_title || "",
        ru_title: storyData.ru_title || "",
        ch_title: storyData.ch_title || "",
        image_url: storyData.image_url || "",
        priority: storyData.priority || 0,
        is_active: storyData.is_active !== undefined ? storyData.is_active : true
      });
      setShowModal(true);
      setError(null);
      setSuccess(null);
    } catch (error) {
      console.error("Error fetching story details:", error);
      setError(`Ошибка загрузки данных сториз: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот сториз?")) return;

    try {
      setLoading(true);
      await deleteStory(storyId);
      setSuccess("Сториз успешно удален");
      fetchStories(pagination.page);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error deleting story:", error);
      setError(`Ошибка удаления: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingItem) {
        await updateStory(editingItem.id, formData);
        setSuccess("Сториз успешно обновлен");
      } else {
        await createStory(formData);
        setSuccess("Сториз успешно создан");
      }
      
      setShowModal(false);
      fetchStories(pagination.page);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error saving story:", error);
      setError(`Ошибка сохранения: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
            Название (дефолт) <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <Input
            type="text"
            value={formData.title || ''}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            className="w-full"
            placeholder="Название сториз"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
              Название (Қазақша)
            </label>
            <Input
              type="text"
              value={formData.kz_title || ''}
              onChange={(e) => setFormData({...formData, kz_title: e.target.value})}
              className="w-full"
              placeholder="Қазақша атау"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
              Название (English)
            </label>
            <Input
              type="text"
              value={formData.eng_title || ''}
              onChange={(e) => setFormData({...formData, eng_title: e.target.value})}
              className="w-full"
              placeholder="English title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
              Название (Русский)
            </label>
            <Input
              type="text"
              value={formData.ru_title || ''}
              onChange={(e) => setFormData({...formData, ru_title: e.target.value})}
              className="w-full"
              placeholder="Русское название"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
              Название (中文)
            </label>
            <Input
              type="text"
              value={formData.ch_title || ''}
              onChange={(e) => setFormData({...formData, ch_title: e.target.value})}
              className="w-full"
              placeholder="中文标题"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
            URL изображения <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <Input
            type="url"
            value={formData.image_url || ''}
            onChange={(e) => setFormData({...formData, image_url: e.target.value})}
            className="w-full"
            placeholder="https://example.com/stories/image.jpg"
            required
          />
          {formData.image_url && (
            <div className="mt-2">
              <img
                src={formData.image_url}
                alt="Preview"
                className="max-w-xs max-h-48 object-contain rounded-lg border border-gray-600"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
              Приоритет
            </label>
            <Input
              type="number"
              value={formData.priority || 0}
              onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
              className="w-full"
              min="0"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Чем выше значение, тем выше приоритет
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
              Статус
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active !== undefined ? formData.is_active : true}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-gray-500 dark:text-gray-300">
                  {formData.is_active ? 'Активен' : 'Неактивен'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded">
            {success}
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="dark-button-secondary"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={loading}
            className="dark-button-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Сохранение...' : (editingItem ? 'Обновить' : 'Создать')}
          </button>
        </div>
      </form>
    );
  };

  const renderTable = () => {
    if (loading && stories.length === 0) {
      return (
        <div className="dark-card p-8 text-center">
          <div className="animate-pulse">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-900 dark:text-white">Загрузка...</p>
          </div>
        </div>
      );
    }

    if (stories.length === 0) {
      return (
        <div className="dark-card p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">Сториз не найдены</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto dark-card">
        <table className="min-w-full">
          <thead className="bg-gray-100 dark:bg-gradient-to-r dark:from-gray-800 dark:to-gray-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ID</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Изображение</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Название</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Приоритет</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Статус</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Создан</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {stories.map((story, index) => (
              <tr key={story.id} className={`hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 ${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800'}`}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">{story.id}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {story.image_url ? (
                    <img
                      src={story.image_url}
                      alt={story.title}
                      className="w-16 h-16 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect width="64" height="64" fill="%23333"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center text-gray-500 text-xs">
                      Нет
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">{story.title}</div>
                  {story.kz_title && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">KZ: {story.kz_title}</div>
                  )}
                  {story.ru_title && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">RU: {story.ru_title}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
                    {story.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {story.is_active ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Активен
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200">
                      Неактивен
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {story.created_at ? new Date(story.created_at).toLocaleDateString('ru-RU') : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(story)}
                      className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors duration-200 px-3 py-1 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900"
                      disabled={loading}
                    >
                      ✏️ Редактировать
                    </button>
                    <button
                      onClick={() => handleDelete(story.id)}
                      className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors duration-200 px-3 py-1 rounded-md hover:bg-red-100 dark:hover:bg-red-900"
                      disabled={loading}
                    >
                      🗑️ Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-6 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Управление сториз</h1>
        
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <label className="text-gray-500 dark:text-gray-300">Фильтр по статусу:</label>
            <select
              value={filters.is_active === undefined ? 'all' : filters.is_active ? 'active' : 'inactive'}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  ...filters,
                  is_active: value === 'all' ? undefined : value === 'active'
                });
              }}
              className="dark-input"
            >
              <option value="all">Все</option>
              <option value="active">Активные</option>
              <option value="inactive">Неактивные</option>
            </select>
          </div>
        </div>

        {/* Add button */}
        <button
          onClick={handleCreate}
          className="mb-4 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-gray-900 dark:text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
        >
          + Добавить сториз
        </button>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded">
            {success}
          </div>
        )}
      </div>

      {/* Table */}
      {renderTable()}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center items-center space-x-4">
          <button
            onClick={() => fetchStories(pagination.page - 1)}
            disabled={pagination.page <= 1 || loading}
            className="dark-button-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Назад
          </button>
          <span className="text-gray-500 dark:text-gray-300">
            Страница {pagination.page} из {pagination.pages} (Всего: {pagination.total})
          </span>
          <button
            onClick={() => fetchStories(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages || loading}
            className="dark-button-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Вперед →
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="dark-card p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {editingItem ? 'Редактировать сториз' : 'Создать сториз'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl transition-colors duration-200"
              >
                ✕
              </button>
            </div>
            {renderForm()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoriesManagement;

